// Type-only: a runtime import would drag accounts.ts's window-touching module
// init into the worker bundle (workers/sidebar.ts imports this module).
import type { StoredAccount } from './accounts';

declare interface myWindow extends Window {
    readonly apiRoot: string;
    readonly SidebarWorkerJS: string;
}
declare const window: myWindow;

// draw updates all the badge counts in the sidebar
// the "keys" are the badge names, values are the counts.
// elements supporting badges have attrs with the name, e.g. `data-badge="orders"`
function draw(bubbleCounts: BubbleCounts) {
    // first lets get all elements that have the badge attr
    const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-badge]'));
    const remaining = new Set(elements);

    // now lets loop through the bubble counts and update the elements
    for (const [key, count] of Object.entries(bubbleCounts)) {
        const elementsWithBadge = elements.filter(el => el.dataset.badge === key);
        if (!elementsWithBadge.length) {
            continue;
        }

        // update the elements with the badge
        for (const el of elementsWithBadge) {
            if (count) {
                el.classList.remove('d-none');
                const badge = el.querySelector('.badge-text');
                if (badge) {
                    badge.textContent = count.toLocaleString();
                }
            } else {
                el.classList.add('d-none');
            }
        }

        // remove from remaining set
        for (const el of elementsWithBadge) {
            remaining.delete(el);
        }
    }

    // now lets hide the remaining elements that are not in the bubble counts
    for (const el of remaining) {
        el.classList.add('d-none');
        const badge = el.querySelector('.badge-text');
        if (badge) {
            badge.textContent = '';
        }
    }
}

export const WorkerAction = {
    setAxiosDefaults: 1,
    get: 3,
    update: 4,
    ping: 5,
    leave: 6,
    active: 7,
    inactive: 8,
} as const;
export type WorkerAction = typeof WorkerAction[keyof typeof WorkerAction];

export declare interface WorkerRequest {
    action: WorkerAction;

    href?: string;
    axiosBaseURL?: string;
    /** `Bearer <encrypted token>` for the tab's pinned account, so the worker
     * fetches bubble counts as that account rather than the cookie default. */
    authorization?: string;
}

export type BubbleCounts = Record<string, number>;

let worker: SharedWorker | undefined;

function send(req: WorkerRequest) {
    worker?.port.postMessage(req);
}

export declare interface BubblesResp {
    counts: Record<string, number>;
}

export function Get() {
    send({ action: WorkerAction.get });
}

export function StartSharedWorker(pinned?: StoredAccount) {
    if (SharedWorker) {
        // Partition the worker per account slot: a SharedWorker is keyed by
        // its URL, so tabs pinned to different accounts get separate workers
        // instead of sharing one cookie-authenticated bubble-count cache.
        let workerURL = window.SidebarWorkerJS;
        if (pinned) {
            const url = new URL(workerURL, window.location.origin);
            url.searchParams.set('u', String(pinned.slot));
            workerURL = url.toString();
        }
        worker = new SharedWorker(workerURL);

        worker.onerror = err => {
            console.error('shared worker error:', err);
        };

        worker.port.onmessage = e => {
            const res = e.data as BubbleCounts;
            draw(res);
        };

        send({
            action: WorkerAction.setAxiosDefaults,
            axiosBaseURL: window.apiRoot,
            authorization: pinned ? `Bearer ${pinned.token}` : undefined,
        });

        send({ action: WorkerAction.get });

        window.addEventListener('unload', () => {
            const req: WorkerRequest = {
                action: WorkerAction.leave,
            };
            worker?.port.postMessage(req);
        });

        setInterval(() => send({ action: WorkerAction.ping }), 1000);
    }
}

export function Update() {
    send({ action: WorkerAction.update });
}

export function SetActive() {
    send({ action: WorkerAction.active });
}

export function SetInactive() {
    send({ action: WorkerAction.inactive });
}